import axios, { AxiosResponse } from 'axios'
import { Context } from '@nuxt/types/app'
import { APIRequest } from '@/infrastructure/network/APIRequest'
import { APIError } from '@/infrastructure/network/APIError'
import { HTTPMethod } from '@/infrastructure/network/types'

export class APIClient {
  baseURL: string = ''
  timeout: number = 60 * 1000

  constructor(private ctx: Context) {
    this.baseURL = process.env.APPLICATION_API || ''
  }

  request<U>(request: APIRequest<U>, onUploadProgress?: any): Promise<U> {
    const isRead = request.method === HTTPMethod.GET
    return new Promise<U>((resolve, reject) => {
      axios
        .request({
          url: 'users' + request.path,
          method: 'POST',
          params: isRead && request.params,
          data: !isRead && this.encodedParams(request),
          timeout: this.timeout,
          baseURL: request.baseURL || this.baseURL,
          headers: this.createHeaders(request),
          maxContentLength: Infinity,
          onUploadProgress,
          withCredentials: true
        })
        .then((data) => {
          if (!process.server || !this.ctx) {
            return data
          }
          const setCookie: string[] | undefined = data.headers['set-cookie']
          if (setCookie) {
            setCookie.forEach((s) => {
              // @ts-ignore
              this.ctx.res.setHeader('set-cookie', s)
            })
          }
          return data
        })
        .then((data) => {
          const response = request.parse ? request.parse(data) : this.parse<U>(data)
          resolve(response)
        })
        .catch((err) => {
          const apiError = this.normalizeError(err)
          reject(apiError)
        })
    })
  }

  // Default parser
  private parse<U>(data: AxiosResponse): U {
    return data.data
  }

  // Convert axios error into APIError
  private normalizeError(error: any): APIError {
    const response = error.response
    const data = response?.data

    return {
      statusCode: response?.status,
      message: data?.message || response?.statusText || error.message || error.raw?.message,
      data: data || undefined,
      errors: data?.errors || {},
      raw: error
    }
  }

  private encodedParams(request: any) {
    if (request.contentType === 'multipart/form-data') {
      const params = new FormData()
      for (const [key, value] of Object.entries<any>(request.params)) {
        if (Array.isArray(value)) {
          value.forEach((prop) => {
            params.append(key + '[]', this.formDataValue(prop))
          })
        } else {
          params.append(key, this.formDataValue(value))
        }
      }
      return params
    }
    return request.params
  }

  private formDataValue(value: any): string | Blob {
    if (typeof value === 'boolean') {
      return value ? '1' : '0'
    }
    return value
  }

  // Create headers
  private createHeaders(request: any) {
    const ctx = this.ctx

    if (!ctx) {
      const header = {}
      return header
    }

    const crypto = require('crypto')
    const clientId = process.env.AUTH_CLIENT_ID
    const secret = process.env.AUTH_SECRET
    const timestamp = String(Date.now())
    const nonce = crypto.createHmac('sha256', secret).update(timestamp).digest('hex')
    const sign = `${clientId}.${timestamp}.${nonce}`
    const signature = crypto.createHmac('sha256', secret).update(sign).digest('hex')

    const tokenType = ctx.app.$cookies.get(process.env.APP_NAME + '_tokenType')
    const accessToken = ctx.app.$cookies.get(process.env.APP_NAME + '_accessToken')
    const Authorization = tokenType ? tokenType + ' ' + accessToken : null

    let header = {
      'Content-Type': request.contentType,
      xhrFields: true,
      'Access-Control-Allow-Origin': '*',
      'X-Requested-With': 'XMLHttpRequest',
      Authorization,
      'X-Auth-Client-Id': clientId,
      'X-Auth-Timestamp': timestamp,
      'X-Auth-Nonce': nonce,
      'X-Auth-Signature': Buffer.from(signature).toString('base64'),
      'X-HTTP-Method-Override': request.method
    } as object

    if (process.server) {
      header = {
        ...header,
        ...ctx.req.headers
      }
    }
    return header
  }
}
